import React, { useState } from "react";
import { CalendarClock, Check, Loader2 } from "lucide-react";
import { toast } from "../../../utils/appToast";

import { cancelRfpPlan } from "../../../services/rfpBillingApi";
import Dialog from "./Dialog";

const KEPT_ITEMS = ["Projects", "Knowledge base", "Proposals"];

// The cancel confirmation both Billing tabs use (RFP Manager below, Extractor Studio in its
// BillingConfirm). `children` is the line saying what stops and when.
export function CancelPlanPanel({ open, onClose, title, keptItems, saving, onConfirm, children }) {
  return (
    <Dialog open={open} onClose={onClose} title={title} maxWidth={460}>
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#FBEFE9] text-[#B33A1E]">
        <CalendarClock size={20} />
      </div>

      <h2 className="mt-4 text-[19px] font-semibold tracking-[-0.01em] text-[#1A1A1A]">{title}</h2>
      <p className="mt-2 text-[13.5px] leading-[1.55] text-[#52525B]">{children}</p>

      <div className="mt-4 rounded-[12px] border border-[#ECE9EE] bg-[#FAF9FB] px-4 py-3.5">
        <p className="text-[11.5px] font-semibold uppercase tracking-[.05em] text-[#8A8A90]">Nothing is deleted</p>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
          {keptItems.map((item) => (
            <span key={item} className="flex items-center gap-1.5 text-[13px] font-medium text-[#3A3A3E]">
              <Check size={14} className="shrink-0 text-[#2F7D44]" />
              {item}
            </span>
          ))}
        </div>
        <p className="mt-2 text-[12.5px] text-[#8A8A90]">Resubscribing unlocks everything again, exactly as you left it.</p>
      </div>

      <div className="mt-6 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onConfirm}
          disabled={saving}
          className="flex items-center gap-1.5 rounded-[9px] px-3.5 py-2 text-[13.5px] font-medium text-[#B33A1E] transition-colors hover:bg-[#FBEFE9] disabled:opacity-50"
        >
          {saving && <Loader2 size={14} className="animate-spin" />}
          {saving ? "Cancelling…" : "Cancel plan"}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-[9px] bg-[#A04A97] px-5 py-2.5 text-[14px] font-semibold text-white shadow-[0_2px_8px_rgba(160,74,151,0.35)] transition-transform hover:bg-[#8F4188] active:scale-[0.98]"
        >
          Keep my plan
        </button>
      </div>
    </Dialog>
  );
}

export default function CancelPlanDialog({ open, onClose, periodEndLabel, onCancelled }) {
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await cancelRfpPlan();
      toast.success("Your plan will end at the close of this billing period.");
      onCancelled?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Couldn't cancel — try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CancelPlanPanel
      open={open}
      onClose={onClose}
      title="Cancel your Premium plan?"
      keptItems={KEPT_ITEMS}
      saving={saving}
      onConfirm={handleConfirm}
    >
      Your team keeps full access until <b className="font-semibold text-[#1A1A1A]">{periodEndLabel}</b>. After
      that, RFP Manager locks for this workspace — nobody can create or open projects until you resubscribe.
    </CancelPlanPanel>
  );
}
